// src/services/documentDatabase.service.js
// Database-backed document service - no localStorage dependencies
import { api } from './api';
import uploadService from './upload.service';

class DocumentDatabaseService {
    constructor() {
        this.baseURL = '/api/documents';
    }

    /**
     * Upload document to Firebase Storage and save metadata to database
     * @param {Object} documentData - Document data
     * @returns {Promise<Object>} - Upload result with download URL
     */
    async uploadToFirebase(content, metadata = {}) {
        try {
            // Create a blob from the content
            const blob = new Blob([content], { type: 'text/plain' });
            const filename = `${metadata.templateName || 'document'}_${Date.now()}.txt`;

            // Upload to Firebase Storage via backend API
            const result = await uploadService.uploadSingle(blob, 'documents', {
                documentType: 'document-builder',
                category: metadata.category || 'company',
                templateName: metadata.templateName,
                employeeId: metadata.employeeId,
                employeeName: metadata.employeeName,
                sentBy: metadata.sentBy || 'HR Department',
                generatedAt: metadata.generatedAt || new Date().toISOString()
            });

            return {
                url: result.downloadURL,
                filePath: result.filePath,
                documentId: result.documentId || result.id
            };
        } catch (error) {
            console.error('Error uploading to Firebase:', error);
            throw error;
        }
    }

    /**
     * Generate PDF and upload to Firebase Storage
     * @param {Object} documentData - Document data
     * @returns {Promise<Object>} - Upload result
     */
    async generateAndUploadPDF(documentData) {
        console.log('🔍 generateAndUploadPDF called with:', {
            hasContent: !!documentData.content,
            contentLength: documentData.content?.length,
            templateName: documentData.templateName,
            employeeName: documentData.employeeName,
            hasMetadata: !!documentData.metadata
        });
        console.log('📄 Content preview (first 500 chars):\n', documentData.content?.substring(0, 500) || 'No content', '\n...');

        try {
            const endpoint = `${this.baseURL}/generate-pdf`;
            console.log('📡 Calling PDF generation API:', endpoint);
            console.log('📤 Sending data:', {
                contentLength: documentData.content?.length,
                templateName: documentData.templateName,
                employeeName: documentData.employeeName
            });

            const response = await api.post(endpoint, {
                content: documentData.content,
                templateName: documentData.templateName,
                employeeName: documentData.employeeName,
                metadata: documentData.metadata
            });

            console.log('✅ PDF generation API response:', {
                status: response.status,
                hasPdfUrl: !!response.data.pdfUrl,
                hasFirebasePath: !!response.data.firebasePath,
                pdfUrl: response.data.pdfUrl
            });

            if (response.data.pdfUrl) {
                console.log('✅ PDF uploaded successfully to Firebase:', response.data.pdfUrl);
                return {
                    pdfUrl: response.data.pdfUrl,
                    firebasePath: response.data.firebasePath,
                    documentId: response.data.documentId
                };
            }

            // If no pdfUrl in response, throw error
            console.error('❌ API returned success but no pdfUrl');
            throw new Error('PDF generation succeeded but no URL was returned');

        } catch (error) {
            console.error('❌ PDF generation API failed:', {
                message: error.message,
                status: error.response?.status,
                statusText: error.response?.statusText,
                data: error.response?.data,
                config: {
                    url: error.config?.url,
                    method: error.config?.method
                }
            });

            // CRITICAL: Throw error instead of silent fallback
            // This ensures user is notified when PDF generation fails
            const errorMessage = error.response?.data?.error ||
                                error.response?.data?.message ||
                                error.message ||
                                'PDF generation failed';

            throw new Error(`Failed to generate PDF: ${errorMessage}`);
        }
    }

    /**
     * Save document metadata to database
     * @param {Object} documentData - Document data
     * @returns {Promise<Object>} - Saved document
     */
    async saveDocument(documentData) {
        try {
            const doc = {
                ...documentData,
                id: documentData.id || `doc_${Date.now()}`,
                createdAt: new Date().toISOString()
            };

            const response = await api.post(`${this.baseURL}`, doc);
            return response.data.document;
        } catch (error) {
            console.error('Error saving document to database:', error);
            throw new Error('Failed to save document to database');
        }
    }

    /**
     * Get document from database
     * @param {String} documentId - Document ID
     * @returns {Promise<Object>} - Document data
     */
    async getDocument(documentId) {
        try {
            const response = await api.get(`${this.baseURL}/${documentId}`);
            return response.data.document;
        } catch (error) {
            console.error('Error getting document:', error);
            throw error;
        }
    }

    /**
     * Send document to employee
     * @param {String} documentId - Document ID
     * @param {Object} recipientData - Recipient data
     * @returns {Promise<Object>} - Send result
     */
    async sendDocument(documentId, recipientData) {
        try {
            const response = await api.post(`${this.baseURL}/${documentId}/send`, {
                employeeId: recipientData.employeeId,
                employeeEmail: recipientData.employeeEmail,
                employeeName: recipientData.employeeName,
                sendViaEmail: recipientData.sendViaEmail || false,
                sendViaPortal: recipientData.sendViaPortal || true,
                metadata: {
                    sentBy: recipientData.sentBy || 'HR Department',
                    sentAt: new Date().toISOString(),
                    department: recipientData.department
                }
            });

            console.log('Document sent successfully via database API:', recipientData.employeeName);
            return response.data;
        } catch (error) {
            console.error('Error sending document:', error);
            throw new Error('Failed to send document');
        }
    }

    /**
     * Get documents for employee
     * @param {String} employeeId - Employee ID
     * @returns {Promise<Array>} - Array of documents
     */
    async getEmployeeDocuments(employeeId) {
        try {
            const response = await api.get(`${this.baseURL}/employee/${employeeId}`);
            return response.data.documents || [];
        } catch (error) {
            console.error('Error getting employee documents:', error);
            return [];
        }
    }

    /**
     * Get company documents
     * @returns {Promise<Array>} - Array of documents
     */
    async getCompanyDocuments() {
        try {
            const response = await api.get(`${this.baseURL}/company`);
            return response.data.documents || [];
        } catch (error) {
            console.error('Error getting company documents:', error);
            return [];
        }
    }

    /**
     * Get user's documents
     * @returns {Promise<Object>} - Object with received, uploaded, company arrays
     */
    async getMyDocuments() {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const employeeId = user.id;
        const userEmail = user.email;

        // Try to get documents sent to this employee
        try {
            const response = await api.get(`${this.baseURL}/my-documents`);
            return {
                received: response.data.received || [],
                uploaded: response.data.uploaded || [],
                company: response.data.company || []
            };
        } catch (error) {
            console.log('my-documents endpoint not available, using alternative methods');

            // Try alternative endpoints
            try {
                // Get documents sent directly to this employee
                const employeeResponse = await api.get(`${this.baseURL}/employee/${employeeId}`);
                const employeeDocs = employeeResponse.data.documents || [];

                // Get all company documents and filter for this employee
                const companyResponse = await api.get(`${this.baseURL}/company`);
                const allCompanyDocs = companyResponse.data.documents || [];
                const docsForThisEmployee = allCompanyDocs.filter(doc =>
                    doc.sentTo === employeeId ||
                    doc.sentToEmail === userEmail ||
                    doc.employeeId === employeeId
                );

                // Combine and remove duplicates
                const allCompany = [...employeeDocs, ...docsForThisEmployee];
                const uniqueCompany = allCompany.filter((doc, index, self) =>
                    index === self.findIndex(d => d.id === doc.id)
                );

                console.log(`Found ${uniqueCompany.length} company documents for ${user.first_name} ${user.last_name}`);

                return {
                    received: [],
                    uploaded: [],
                    company: uniqueCompany
                };
            } catch (error2) {
                console.error('All document endpoints failed:', error2);
                return { received: [], uploaded: [], company: [] };
            }
        }
    }

    /**
     * Generate document
     * @param {Object} documentData - Document data
     * @returns {Promise<Object>} - Generated document
     */
    async generateDocument(documentData) {
        const doc = await this.saveDocument(documentData);
        console.log('Document generated and saved to database:', documentData.templateName);
        return doc;
    }

    /**
     * Get generated documents
     * @returns {Promise<Array>} - Array of documents
     */
    async getGeneratedDocuments() {
        try {
            const response = await api.get(`${this.baseURL}/generated`);
            return response.data.documents || [];
        } catch (error) {
            console.error('Error getting generated documents:', error);
            return [];
        }
    }

    /**
     * Get drafts
     * @returns {Promise<Array>} - Array of drafts
     */
    async getDrafts() {
        try {
            const response = await api.get(`${this.baseURL}/drafts`);
            return response.data.drafts || [];
        } catch (error) {
            console.error('Error getting drafts:', error);
            return [];
        }
    }

    /**
     * Delete document
     * @param {String} documentId - Document ID
     * @param {String} firebasePath - Firebase storage path (if applicable)
     * @returns {Promise<Object>} - Delete result
     */
    async deleteDocument(documentId, firebasePath = null) {
        try {
            // Delete from database
            const response = await api.delete(`${this.baseURL}/${documentId}`);

            // Delete from Firebase Storage if path provided
            if (firebasePath) {
                await api.delete(`${this.baseURL}/firebase/${encodeURIComponent(firebasePath)}`);
            }

            return response.data;
        } catch (error) {
            console.error('Error deleting document:', error);
            throw new Error('Failed to delete document');
        }
    }

    /**
     * Update document status
     * @param {String} documentId - Document ID
     * @param {String} status - New status
     * @returns {Promise<Object>} - Update result
     */
    async updateDocumentStatus(documentId, status) {
        try {
            const response = await api.patch(`${this.baseURL}/${documentId}/status`, { status });
            return response.data;
        } catch (error) {
            console.error('Error updating document status:', error);
            throw new Error('Failed to update document status');
        }
    }

    /**
     * Search documents
     * @param {Object} searchParams - Search parameters
     * @returns {Promise<Array>} - Array of documents
     */
    async searchDocuments(searchParams) {
        try {
            const response = await api.get(`${this.baseURL}/search`, { params: searchParams });
            return response.data.documents || [];
        } catch (error) {
            console.error('Error searching documents:', error);
            return [];
        }
    }
}

export default new DocumentDatabaseService();